/**
 * useDestinationInfo Hook
 * Handles fetching destination health information
 */

import { useState, useCallback } from "react";
import { Destination, DestinationHealthInfo } from "@/types";
import { getDestinationInfoByCountry } from "@/services/destinations";

export interface UseDestinationInfoReturn {
  destinationInfoMap: Record<string, DestinationHealthInfo>;
  loadingDestinations: boolean;
  expandedDestinations: Record<number, boolean>;
  fetchDestinationInfo: (destinations: Destination[]) => Promise<void>;
  toggleDestination: (index: number) => void;
}

export function useDestinationInfo(): UseDestinationInfoReturn {
  const [destinationInfoMap, setDestinationInfoMap] = useState<
    Record<string, DestinationHealthInfo>
  >({});
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [expandedDestinations, setExpandedDestinations] = useState<
    Record<number, boolean>
  >({});

  const fetchDestinationInfo = useCallback(async (destinations: Destination[]) => {
    setLoadingDestinations(true);
    const infoMap: Record<string, DestinationHealthInfo> = {};

    try {
      const uniqueCountries = new Set<string>();
      destinations.forEach((dest) => {
        if (dest.destination_country) {
          uniqueCountries.add(dest.destination_country);
        }
      });

      await Promise.all(
        Array.from(uniqueCountries).map(async (country) => {
          try {
            const info = await getDestinationInfoByCountry(country, 1);
            infoMap[country] = info;
          } catch (error) {
            // Silent fail for individual country
          }
        })
      );

      setDestinationInfoMap(infoMap);
    } catch (error) {
      // Silent fail
    } finally {
      setLoadingDestinations(false);
    }
  }, []);

  const toggleDestination = useCallback((index: number) => {
    setExpandedDestinations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  return {
    destinationInfoMap,
    loadingDestinations,
    expandedDestinations,
    fetchDestinationInfo,
    toggleDestination,
  };
}
